import React, { useEffect, useState } from 'react';

import {
  Gallery,
  GallerySlide,
  Lentils,
  useIsMobile,
} from '@vw-marketing/us-components';

import { ContentCard } from './ContentCard';
import { fetchOwnerManualToken } from '../../fetch-data/fetch-owner-manual-token';
import { getStringifiedServiceConfigsServiceConfig } from '../../hooks/get-stringified-service-configs-service-config';
import { IMAGE_COLLECTIONS } from './imageConfig';
import { RelatedContentCardsContainer, StyledLentilsContainer } from './styled';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';

const ID4_REGEX = /^ID\.?\s*4/i;
const IDBUZZ_REGEX = /^ID\.?\s*BUZZ/i;

const CONTENT_CARDS = [
  {
    key: 'OWNER_LITERATURE',
    translationKey: 'owners.literature',
    ariaLabel: "Related content slide Order Owner's Literature",
    url: 'https://literature.vw.com/welcome.asp',
  },
  {
    key: 'OWNER_MANUAL',
    translationKey: 'owners.manual',
    ariaLabel: 'Related content slide View Owners Manual',
    requiresVin: true,
    requiresToken: true,
    urlTemplate:
      'https://www.ownersmanualvw.com/#/vin/{vin}/token/{token}/lang/en-us',
  },
  {
    key: 'CA_EMISSIONS',
    translationKey: 'california.emissions',
    ariaLabel: 'Related content slide California Emissions Parts',
    shouldShow: (model: string) =>
      !ID4_REGEX.test(model) && !IDBUZZ_REGEX.test(model),
    url: 'https://www.vw.com/en/ca-parts.html',
  },
  {
    key: 'BATTERY_MANUAL',
    translationKey: 'battery.manual',
    ariaLabel: 'Related content slide Explore Battery Charging Cable Manual',
    shouldShow: (model: string) =>
      ID4_REGEX.test(model) && !IDBUZZ_REGEX.test(model),
    disableIfBuzz: true,
    url: 'https://simplepart.app.box.com/s/6u40gr8c3y8edc3wv8iuhpzjq55c8u7q',
  },
];

export const RelatedContentCards = ({
  setLiteratureData,
  apiConfig
}: {
  setLiteratureData: (value: any) => void;
  apiConfig: string;
}) => {
  const { translationHandler } = useMaintenanceContext();
  const isMobile = useIsMobile(600);
  const [store] = useStore();
  const { currentData } = store;
  const { model = '', vin } = currentData;

  const {
    'service-config-provider': serviceProvider,
    's2:server-request': serverRequest,
  } = useFeatureServices();
  const { configs } = serviceProvider;
  const faServicesUrl = configs['fa-services']?.urlOrigin ?? '';
  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceProvider,
    serverRequest,
  );

  const contentCards = CONTENT_CARDS.filter(
    (card) => !card.shouldShow || card.shouldShow(model || ''),
  );

  const getTranslation = (key: string) =>
    translationHandler(`literature.downloadables.relatedContent.${key}`);

  const renderContentCard = (
    cardData: (typeof CONTENT_CARDS)[0],
    index?: number,
  ) => {
    const isDisabledByVin = cardData.requiresVin && !vin;
    const isDisabledForBuzz =
      cardData.disableIfBuzz && IDBUZZ_REGEX.test(model);
    const isButtonDisabled = isDisabledByVin || isDisabledForBuzz;

    let redirectUrl = cardData.url;

    const [cardUrl, setCardUrl] = useState<string | undefined>(redirectUrl);

    useEffect(() => {
      if (cardData.urlTemplate && vin && cardData.requiresToken) {
        fetchOwnerManualToken({
          faServicesUrl,
          apiKey: apiConfig,
          vin,
          serviceConfigsServiceConfig,
        }).then((data) => {
          const formattedUrl = cardData.urlTemplate
            .replace('{vin}', vin)
            .replace('{token}', data.token);
          setCardUrl(formattedUrl);
        });
      }
    }, [cardData, vin, faServicesUrl, serviceConfigsServiceConfig]);

    return (
      <ContentCard
        images={IMAGE_COLLECTIONS[cardData.key]}
        key={index! + 1}
        redirectName={getTranslation(`${cardData.translationKey}.redirect`)}
        title={getTranslation(cardData.translationKey)}
        url={cardUrl!}
        isDisabled={isButtonDisabled}
        setLiteratureData={setLiteratureData}
      />
    );
  };

  if (!isMobile) {
    return (
      <RelatedContentCardsContainer>
        {contentCards.map((cardData, index) =>
          renderContentCard(cardData, index),
        )}
      </RelatedContentCardsContainer>
    );
  }

  return (
    <RelatedContentCardsContainer>
      <Gallery
        id="related-content-gallery"
        aria-label="Related Content gallery"
        bottomElements={
          <StyledLentilsContainer>
            <Lentils />
          </StyledLentilsContainer>
        }
        gutter="static350"
        slidesPerPage={1.32}
      >
        {contentCards.map((cardData, index) => (
          <GallerySlide aria-label={cardData.ariaLabel} key={index.toString()}>
            {renderContentCard(cardData, index)}
          </GallerySlide>
        ))}
      </Gallery>
    </RelatedContentCardsContainer>
  );
};
